import type { FC, ReactNode } from 'react'
import type { Testimonial } from '@/types'

import { useRef } from 'react'
import { Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { gsap, MOTION, useGSAP } from '@/lib/motion'
import { SplitWords } from '@/components/generals/split-words'

const HOUSE_CLIP = 'polygon(0% 36%, 50% 0%, 100% 36%, 100% 100%, 0% 100%)'

const OFFSETS = [
    'md:col-span-9 md:col-start-1',
    'md:col-span-8 md:col-start-5',
    'md:col-span-9 md:col-start-2',
    'md:col-span-8 md:col-start-4'
]

interface TestimonialsSectionProps {
    testimonials: Array<Testimonial>
}

/** Chapter eight, on mist: the clients' own words, staggered like a pinned-up wall, portraits trailing the pointer. */
export const TestimonialsSection: FC<TestimonialsSectionProps> = ({ testimonials }): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const followerRef = useRef<HTMLDivElement>(null)
    const followerImageRef = useRef<HTMLImageElement>(null)

    useGSAP(() => {
        const section = ref.current
        const follower = followerRef.current
        const followerImage = followerImageRef.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add(MOTION.ok, () => {
            gsap.fromTo(q('[data-title] [data-word]'), { yPercent: 110 }, {
                yPercent: 0,
                duration: 1.2,
                stagger: 0.06,
                ease: 'expo.out',
                scrollTrigger: { trigger: q('[data-title]')[0], start: 'top 82%', toggleActions: 'play none none reverse' }
            })

            q('[data-quote]').forEach(quote => {
                gsap.timeline({
                    scrollTrigger: { trigger: quote, start: 'top 82%', toggleActions: 'play none none reverse' }
                })
                    .fromTo(quote.querySelector('[data-quote-text]'), { clipPath: 'inset(0% 0% 100% 0%)', y: 60 }, { clipPath: 'inset(0% 0% 0% 0%)', y: 0, duration: 1.3, ease: 'expo.out' }, 0)
                    .fromTo(quote.querySelector('[data-quote-mark]'), { scale: 0, rotate: -30 }, { scale: 1, rotate: 0, duration: 0.9, ease: 'back.out(1.6)' }, 0.1)
                    .fromTo(quote.querySelector('[data-quote-meta]'), { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power3.out' }, 0.35)
            })
        })

        mm.add(`${MOTION.ok} and ${MOTION.fine}`, () => {
            if (!follower || !followerImage) return

            gsap.set(follower, { xPercent: -50, yPercent: -60, scale: 0, autoAlpha: 0 })

            const toX = gsap.quickTo(follower, 'x', { duration: 0.7, ease: 'power3.out' })
            const toY = gsap.quickTo(follower, 'y', { duration: 0.7, ease: 'power3.out' })
            const toRotate = gsap.quickTo(follower, 'rotate', { duration: 0.8, ease: 'power3.out' })
            let lastX = 0

            const onMove = (event: PointerEvent) => {
                toX(event.clientX)
                toY(event.clientY)
                toRotate(gsap.utils.clamp(-12, 12, (event.clientX - lastX) * 0.6))
                lastX = event.clientX
            }

            const cleanups = q('[data-quote]').map(quote => {
                const photo = quote.dataset.photo

                const onEnter = (event: PointerEvent) => {
                    if (!photo) return

                    followerImage.src = photo
                    lastX = event.clientX
                    gsap.set(follower, { x: event.clientX, y: event.clientY })
                    gsap.to(follower, { scale: 1, autoAlpha: 1, duration: 0.6, ease: 'expo.out', overwrite: 'auto' })
                }

                const onLeave = () => {
                    gsap.to(follower, { scale: 0, autoAlpha: 0, duration: 0.4, ease: 'power3.in', overwrite: 'auto' })
                }

                quote.addEventListener('pointerenter', onEnter)
                quote.addEventListener('pointerleave', onLeave)

                return () => {
                    quote.removeEventListener('pointerenter', onEnter)
                    quote.removeEventListener('pointerleave', onLeave)
                }
            })

            window.addEventListener('pointermove', onMove, { passive: true })

            return () => {
                window.removeEventListener('pointermove', onMove)
                cleanups.forEach(cleanup => cleanup())
            }
        })

        return () => mm.revert()
    }, { scope: ref, dependencies: [testimonials.length] })

    return (
        <section
            ref={ref}
            id='testimonials'
            data-tone='mist'
            data-surface='light'
            aria-labelledby='testimonials-title'
            className='relative bg-mist px-(--gutter) pb-[18vh] pt-[18vh] text-ink'
        >
            <div className='grid items-end gap-x-8 gap-y-6 md:grid-cols-12'>
                <p className='label md:col-span-3 md:pb-4'>
                    <span className='mr-3'>06</span>
                    Testimonials
                </p>

                <h2
                    id='testimonials-title'
                    data-title=''
                    className='display text-[clamp(3rem,8.4vw,10rem)] leading-[0.86] wdth-75 md:col-span-9'
                >
                    <SplitWords text="How we've helped" />
                </h2>
            </div>

            {testimonials.length > 0 ? (
                <ol className='mt-[12vh] space-y-[11vh]'>
                    {testimonials.map((testimonial, index) => (
                        <li key={testimonial.id} className='grid gap-x-8 md:grid-cols-12'>
                            <figure
                                data-quote=''
                                data-photo={testimonial.photo ?? undefined}
                                className={cn('relative', OFFSETS[index % OFFSETS.length])}
                            >
                                <span
                                    data-quote-mark=''
                                    aria-hidden='true'
                                    className='display absolute -left-1 -top-[0.42em] text-[clamp(4rem,7vw,7.5rem)] leading-none text-signal md:-left-[0.55em]'
                                >
                                    &ldquo;
                                </span>

                                <blockquote
                                    data-quote-text=''
                                    className='pt-6 text-[clamp(1.55rem,3.1vw,3.1rem)] font-medium leading-[1.14] tracking-[-0.022em] wdth-87 md:pt-0'
                                >
                                    <p>
                                        <span data-mdlfy-cms={`testimonials:${testimonial.id}:quote`}>{testimonial.quote}</span>
                                    </p>
                                </blockquote>

                                <figcaption data-quote-meta='' className='hairline mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 border-t pt-5'>
                                    {testimonial.photo && (
                                        <img
                                            src={testimonial.photo}
                                            alt={testimonial.photoAlt ?? ''}
                                            width={56}
                                            height={56}
                                            loading='lazy'
                                            decoding='async'
                                            className='size-14 object-cover'
                                            style={{ clipPath: HOUSE_CLIP }}
                                        />
                                    )}

                                    <span className='flex flex-col'>
                                        <span className='text-lg font-semibold'>
                                            <span data-mdlfy-cms={`testimonials:${testimonial.id}:name`}>{testimonial.name}</span>
                                        </span>

                                        {testimonial.business && (
                                            <span className='label mt-1 text-stone'>
                                                <span data-mdlfy-cms={`testimonials:${testimonial.id}:business`}>{testimonial.business}</span>
                                            </span>
                                        )}
                                    </span>

                                    <span className='ml-auto flex items-center gap-1' role='img' aria-label={`Rated ${testimonial.rating} out of 5`}>
                                        {Array.from({ length: 5 }, (_, star) => (
                                            <Star
                                                key={star}
                                                aria-hidden='true'
                                                className={cn('size-4', star < testimonial.rating ? 'fill-ink text-ink' : 'text-ink/25')}
                                            />
                                        ))}
                                    </span>
                                </figcaption>
                            </figure>
                        </li>
                    ))}
                </ol>
            ) : (
                <p className='mt-[10vh] max-w-[40ch] text-lg text-stone'>
                    Client stories will appear here soon.
                </p>
            )}

            <div
                ref={followerRef}
                aria-hidden='true'
                className='pointer-events-none fixed left-0 top-0 z-[60] hidden w-44 drop-shadow-[0_24px_30px_rgba(21,30,33,0.35)] [@media(hover:hover)_and_(pointer:fine)]:block'
                style={{ opacity: 0 }}
            >
                <img
                    ref={followerImageRef}
                    alt=''
                    className='aspect-square w-full object-cover'
                    style={{ clipPath: HOUSE_CLIP }}
                />
            </div>
        </section>
    )
}
